import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import {
  FiChevronLeft,
  FiChevronRight,
  FiCrop,
  FiMinus,
  FiPlus,
  FiRefreshCcw,
  FiRotateCcw,
  FiRotateCw,
  FiSave,
  FiX
} from 'react-icons/fi'
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { useAppToast } from '../../helpers'
import ContestsService from '../../services/Contests/ContestsService'
import { Tooltip } from '../Tooltip'

interface IArtworkPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  imageUrl: string | null
  imageAlt?: string
  onPrev?: () => void
  onNext?: () => void
  positionLabel?: string
  artworkId?: number
  onRotationSaved?: (newUrl: string) => void
  onCropSaved?: (newUrl: string) => void
}

const service = new ContestsService()

export default function ArtworkPreviewModal({
  isOpen,
  onClose,
  imageUrl,
  imageAlt,
  onPrev,
  onNext,
  positionLabel,
  artworkId,
  onRotationSaved,
  onCropSaved
}: IArtworkPreviewModalProps) {
  const [currentUrl, setCurrentUrl] = useState<string | null>(imageUrl)
  const [previewRotation, setPreviewRotation] = useState(0)
  const [savedRotation, setSavedRotation] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  // Crop state
  const [isCropMode, setIsCropMode] = useState(false)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const imgRef = useRef<HTMLImageElement>(null)
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null)

  const toast = useAppToast()

  const hasUnsavedRotation = previewRotation % 360 !== savedRotation % 360
  const hasCropSelection = !!completedCrop && completedCrop.width > 0 && completedCrop.height > 0

  useEffect(() => {
    setCurrentUrl(imageUrl)
    setPreviewRotation(0)
    setSavedRotation(0)
    setIsCropMode(false)
    setCrop(undefined)
    setCompletedCrop(undefined)
    setNaturalSize(null)
  }, [isOpen, imageUrl])

  const handleSaveRotation = async () => {
    if (!artworkId) return
    const degrees = (((previewRotation - savedRotation) % 360) + 360) % 360
    setIsSaving(true)
    try {
      const { artwork_url } = await service.rotateArtwork(artworkId, degrees)
      setSavedRotation(previewRotation)
      setCurrentUrl(artwork_url)
      onRotationSaved?.(artwork_url)
      toast.success('Rotação salva com sucesso!')
    } catch {
      toast.error('Erro ao salvar a rotação. Tente novamente.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEnterCropMode = () => {
    if (hasUnsavedRotation) return
    setCrop(undefined)
    setCompletedCrop(undefined)
    setIsCropMode(true)
  }

  const handleCancelCrop = () => {
    setCrop(undefined)
    setCompletedCrop(undefined)
    setIsCropMode(false)
  }

  const handleSaveCrop = async () => {
    if (!artworkId || !completedCrop || !naturalSize || !imgRef.current) return

    const rendered = imgRef.current.getBoundingClientRect()
    const scaleX = naturalSize.width / rendered.width
    const scaleY = naturalSize.height / rendered.height

    const naturalCrop = {
      x: Math.round(completedCrop.x * scaleX),
      y: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY)
    }

    setIsSaving(true)
    try {
      const { artwork_url } = await service.cropArtwork(artworkId, naturalCrop)
      setCurrentUrl(artwork_url)
      onCropSaved?.(artwork_url)
      setIsCropMode(false)
      setCrop(undefined)
      setCompletedCrop(undefined)
      toast.success('Recorte salvo com sucesso!')
    } catch {
      toast.error('Erro ao salvar o recorte. Tente novamente.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay backdropFilter="blur(6px)" bg="rgba(17,24,39,0.85)" />
      <ModalContent bg="transparent" shadow="none">
        <ModalCloseButton
          color="white"
          zIndex={3}
          bg="blackAlpha.600"
          borderRadius="full"
          _hover={{ bg: 'blackAlpha.800' }}
        />
        <ModalBody p={0} display="flex" justifyContent="center" alignItems="center" h="90vh">
          {currentUrl && (
            <Box position="relative" w="100%" h="100%">
              {/* ── Normal view (zoom/pan) ── */}
              {!isCropMode && (
                <TransformWrapper
                  initialScale={1}
                  minScale={1}
                  maxScale={8}
                  wheel={{ step: 0.2 }}
                  doubleClick={{ mode: 'toggle', step: 2 }}
                  pinch={{ step: 5 }}
                  centerOnInit
                >
                  {({ zoomIn, zoomOut, resetTransform }) => (
                    <>
                      <TransformComponent
                        wrapperStyle={{ width: '100%', height: '100%' }}
                        contentStyle={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <Image
                          src={currentUrl}
                          alt={imageAlt || 'Obra em tamanho completo'}
                          maxH="88vh"
                          maxW="100%"
                          objectFit="contain"
                          borderRadius="md"
                          userSelect="none"
                          draggable={false}
                          sx={{
                            transform: `rotate(${previewRotation}deg)`,
                            transition: 'transform .25s ease'
                          }}
                        />
                      </TransformComponent>

                      {/* Prev chevron */}
                      {onPrev && (
                        <Tooltip label="Obra anterior" placement="right">
                          <IconButton
                            aria-label="Obra anterior"
                            icon={<FiChevronLeft size={28} />}
                            onClick={onPrev}
                            position="absolute"
                            left="12px"
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex={2}
                            size="lg"
                            variant="ghost"
                            color="white"
                            bg="blackAlpha.500"
                            borderRadius="full"
                            disabled={isSaving}
                            _hover={{ bg: 'blackAlpha.700' }}
                          />
                        </Tooltip>
                      )}

                      {/* Next chevron */}
                      {onNext && (
                        <Tooltip label="Próxima obra" placement="left">
                          <IconButton
                            aria-label="Próxima obra"
                            icon={<FiChevronRight size={28} />}
                            onClick={onNext}
                            position="absolute"
                            right="12px"
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex={2}
                            size="lg"
                            variant="ghost"
                            color="white"
                            bg="blackAlpha.500"
                            borderRadius="full"
                            _hover={{ bg: 'blackAlpha.700' }}
                            disabled={isSaving}
                          />
                        </Tooltip>
                      )}

                      {/* Zoom + rotation toolbar */}
                      <Flex
                        position="absolute"
                        bottom="16px"
                        left="50%"
                        transform="translateX(-50%)"
                        gap="2"
                        bg="rgba(17,24,39,0.85)"
                        backdropFilter="blur(8px)"
                        borderRadius="full"
                        px="3"
                        py="2"
                        boxShadow="0 8px 24px rgba(0,0,0,0.35)"
                        zIndex={2}
                        align="center"
                      >
                        <Tooltip label="Reduzir zoom">
                          <Button
                            aria-label="Reduzir zoom"
                            onClick={() => zoomOut()}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            borderRadius="full"
                            minW="36px"
                            h="36px"
                            p="0"
                          >
                            <FiMinus />
                          </Button>
                        </Tooltip>
                        <Tooltip label="Ampliar zoom">
                          <Button
                            aria-label="Ampliar zoom"
                            onClick={() => zoomIn()}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            borderRadius="full"
                            minW="36px"
                            h="36px"
                            p="0"
                          >
                            <FiPlus />
                          </Button>
                        </Tooltip>
                        <Box w="1px" bg="whiteAlpha.300" mx="1" />
                        <Tooltip label="Girar -90°">
                          <Button
                            aria-label="Girar para a esquerda"
                            onClick={() => setPreviewRotation((r) => (r - 90 + 360) % 360)}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            borderRadius="full"
                            minW="36px"
                            h="36px"
                            p="0"
                          >
                            <FiRotateCcw />
                          </Button>
                        </Tooltip>
                        <Tooltip label="Girar +90°">
                          <Button
                            aria-label="Girar para a direita"
                            onClick={() => setPreviewRotation((r) => (r + 90) % 360)}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            borderRadius="full"
                            minW="36px"
                            h="36px"
                            p="0"
                          >
                            <FiRotateCw />
                          </Button>
                        </Tooltip>
                        <Box w="1px" bg="whiteAlpha.300" mx="1" />
                        <Tooltip label="Resetar visualização">
                          <Button
                            aria-label="Resetar visualização"
                            onClick={() => {
                              resetTransform()
                              setPreviewRotation(savedRotation)
                            }}
                            size="sm"
                            variant="ghost"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            borderRadius="full"
                            minW="36px"
                            h="36px"
                            p="0"
                          >
                            <FiRefreshCcw />
                          </Button>
                        </Tooltip>
                        {artworkId && hasUnsavedRotation && (
                          <>
                            <Box w="1px" bg="whiteAlpha.300" mx="1" />
                            <Tooltip label="Salvar rotação">
                              <Button
                                aria-label="Salvar rotação"
                                onClick={handleSaveRotation}
                                isLoading={isSaving}
                                loadingText="Salvando..."
                                size="sm"
                                variant="solid"
                                colorScheme="blue"
                                borderRadius="full"
                                leftIcon={<FiSave />}
                                h="36px"
                                px="3"
                              >
                                Salvar rotação
                              </Button>
                            </Tooltip>
                          </>
                        )}
                        {artworkId && (
                          <>
                            <Box w="1px" bg="whiteAlpha.300" mx="1" />
                            <Tooltip
                              label={hasUnsavedRotation ? 'Salve a rotação antes de recortar' : 'Recortar imagem'}
                            >
                              <Button
                                aria-label="Recortar imagem"
                                onClick={handleEnterCropMode}
                                isDisabled={isSaving || hasUnsavedRotation}
                                size="sm"
                                variant="ghost"
                                color={hasUnsavedRotation ? 'whiteAlpha.400' : 'white'}
                                _hover={hasUnsavedRotation ? {} : { bg: 'whiteAlpha.200' }}
                                borderRadius="full"
                                leftIcon={<FiCrop />}
                                h="36px"
                                px="3"
                              >
                                Recortar
                              </Button>
                            </Tooltip>
                          </>
                        )}
                        {positionLabel && (
                          <>
                            <Box w="1px" bg="whiteAlpha.300" mx="1" />
                            <Text fontSize="12px" color="whiteAlpha.800" fontWeight="500" px="1" whiteSpace="nowrap">
                              {positionLabel}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </>
                  )}
                </TransformWrapper>
              )}

              {/* ── Crop view ── */}
              {isCropMode && (
                <Flex w="100%" h="100%" direction="column" align="center" justify="center" gap="4">
                  <Text fontSize="13px" color="whiteAlpha.700" fontWeight="500">
                    Arraste para selecionar a área de recorte
                  </Text>

                  <Box
                    maxH="75vh"
                    maxW="100%"
                    overflow="hidden"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      keepSelection
                      style={{ maxHeight: '75vh', maxWidth: '100%' }}
                    >
                      <img
                        ref={imgRef}
                        src={currentUrl}
                        alt={imageAlt || 'Obra em tamanho completo'}
                        style={{ maxHeight: '75vh', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                        onLoad={(e) => {
                          const el = e.currentTarget
                          setNaturalSize({ width: el.naturalWidth, height: el.naturalHeight })
                        }}
                        draggable={false}
                      />
                    </ReactCrop>
                  </Box>

                  {/* Crop toolbar */}
                  <Flex
                    gap="2"
                    bg="rgba(17,24,39,0.85)"
                    backdropFilter="blur(8px)"
                    borderRadius="full"
                    px="4"
                    py="2"
                    boxShadow="0 8px 24px rgba(0,0,0,0.35)"
                    align="center"
                  >
                    <Button
                      onClick={handleCancelCrop}
                      size="sm"
                      variant="ghost"
                      color="whiteAlpha.800"
                      _hover={{ bg: 'whiteAlpha.200' }}
                      borderRadius="full"
                      leftIcon={<FiX />}
                      h="36px"
                      px="4"
                      isDisabled={isSaving}
                    >
                      Cancelar
                    </Button>
                    <Box w="1px" bg="whiteAlpha.300" mx="1" />
                    <Button
                      onClick={handleSaveCrop}
                      isLoading={isSaving}
                      loadingText="Salvando..."
                      isDisabled={!hasCropSelection}
                      size="sm"
                      variant="solid"
                      colorScheme="green"
                      borderRadius="full"
                      leftIcon={<FiSave />}
                      h="36px"
                      px="4"
                    >
                      Salvar recorte
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
